import { useState, useEffect, FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Modal } from "../../components/Modal/Modal";
import { Input } from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { useToast } from "../../components/Toast/Toast";
import { ApiError, describeError } from "../../lib/errors";
import { patchProject, ProjectDetail } from "./api";

interface Props {
  open: boolean;
  onClose: () => void;
  project: ProjectDetail;
  /** ETag for the optimistic-concurrency PATCH; falls back to project.etag. */
  etag?: string | null;
  /** Notify caller after a successful rename so it can refresh dependent data. */
  onSaved?: (project: ProjectDetail) => void;
}

// Mockup L464 ‒ "Rename" is the primary affordance on the project hero. The
// project-name input shares the same validation rules as Create/Config (2–80
// chars; ASCII alphanumerics + space + hyphen). Save sends an If-Match'd PATCH
// and surfaces 409 etag_mismatch via toast so the user knows to reload.
const NAME_PATTERN = /^[A-Za-z0-9 \-]+$/;

export function RenameProjectModal({
  open,
  onClose,
  project,
  etag,
  onSaved,
}: Props) {
  const qc = useQueryClient();
  const { push } = useToast();
  const [name, setName] = useState(project.name);
  const [error, setError] = useState<string | null>(null);

  // Sync local state when the modal is (re)opened or the underlying project
  // refetches with a new server-side name (e.g. after a 409 reload).
  useEffect(() => {
    if (open) {
      setName(project.name);
      setError(null);
    }
  }, [open, project.name]);

  const trimmed = name.trim();
  const valid =
    trimmed.length >= 2 &&
    trimmed.length <= 80 &&
    NAME_PATTERN.test(trimmed);
  const dirty = trimmed !== project.name;

  const mut = useMutation({
    mutationFn: async () => {
      // Strip weak-validator + surrounding quotes before sending the raw token.
      const raw = (etag ?? project.etag ?? "")
        .replace(/^W\//, "")
        .replace(/^"|"$/g, "");
      const r = await patchProject(project.id, { name: trimmed }, raw);
      return r;
    },
    onSuccess: async (r) => {
      push({ variant: "success", message: "Project renamed." });
      // Invalidate everything that displays the project name so dependent
      // surfaces (overview, list, breadcrumb) refresh on next render.
      await qc.invalidateQueries({ queryKey: ["project", project.id] });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await qc.invalidateQueries({
        queryKey: ["project", project.id, "activity"],
      });
      onSaved?.(r.data);
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "etag_mismatch") {
        push({
          variant: "warn",
          message:
            "Project was updated by someone else — reload to see the latest version.",
        });
        // Force a refetch and close — the caller will see fresh data.
        qc.invalidateQueries({ queryKey: ["project", project.id] });
        onClose();
        return;
      }
      setError(
        err instanceof ApiError
          ? describeError(err.code)
          : describeError("internal"),
      );
    },
  });

  function submit() {
    setError(null);
    if (!valid) {
      setError(
        "Name must be 2–80 characters and contain only letters, numbers, spaces, or hyphens.",
      );
      return;
    }
    if (!dirty) {
      onClose();
      return;
    }
    mut.mutate();
  }

  function onFormSubmit(e: FormEvent) {
    e.preventDefault();
    submit();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Rename project"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={mut.isPending || !valid || !dirty}
          >
            {mut.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <form onSubmit={onFormSubmit}>
        {error && <Alert variant="danger">{error}</Alert>}
        <Input
          label="Project name"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          hint="2–80 characters. Letters, numbers, spaces, hyphens."
          required
        />
      </form>
    </Modal>
  );
}
